<script setup>
import { i18n } from 'boot/i18n';
import { HrCheckbox, HrOptionGroup, HrTooltip } from 'src/design-system';
import { computed } from 'vue';

import { DEFAULT_STRICTNESS_MODE, STRICTNESS_MODES } from '../../domain/constants/aiAuditorSettings';
import { useAuditorAccess } from '../composables/useAuditorAccess';

defineOptions({ name: 'AuditorTemplateSettings' });

const props = defineProps({
  enabled: {
    type: Boolean,
    default: false,
  },
  strictness: {
    type: String,
    default: null,
  },
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:enabled', 'update:strictness']);

const { canOperateAuditor, hasAuditorEntitlement, isStaffBypass } = useAuditorAccess();

const isDisabled = computed(() => props.disable || !canOperateAuditor.value);

const strictnessOptions = STRICTNESS_MODES.map(({ value, labelKey, isDefault }) => ({
  value,
  label: i18n.t(labelKey),
  isDefault,
}));

const selectedStrictness = computed(() => (
  STRICTNESS_MODES.some(({ value }) => value === props.strictness)
    ? props.strictness
    : DEFAULT_STRICTNESS_MODE
));
</script>

<template>
  <div class="column q-gutter-y-sm">
    <p class="hr-body-medium hr-text-on-surface-variant q-mb-none">
      {{ i18n.t('noteAuditor.templateSettings.textDescription') }}
    </p>
    <hr-checkbox
      :model-value="props.enabled"
      data-e2e="checkbox-enable-note-auditor"
      :disable="isDisabled"
      @update:model-value="emit('update:enabled', $event)"
    >
      {{ i18n.t('noteAuditor.templateSettings.checkboxEnable') }}
      <hr-tooltip v-if="!hasAuditorEntitlement">
        {{ isStaffBypass
          ? i18n.t('noteAuditor.tooltipStaffBypass')
          : i18n.t('noteAuditor.tooltipSubscriptionUnavailable') }}
      </hr-tooltip>
    </hr-checkbox>
    <fieldset
      v-if="props.enabled"
      class="auditor-template-settings__strictness"
    >
      <legend class="hr-label-large hr-text-on-surface q-mb-sm">
        {{ i18n.t('noteAuditor.auditSettingsForm.strictnessModeHeading') }}
      </legend>
      <hr-option-group
        :model-value="selectedStrictness"
        :options="strictnessOptions"
        type="radio"
        :disable="isDisabled"
        @update:model-value="emit('update:strictness', $event)"
      >
        <template #label="option">
          <span :data-e2e="`radio-auditor-strictness-${option.value}`">
            {{ option.label }}
            <span
              v-if="option.isDefault"
              class="hr-body-small hr-text-on-surface-variant"
            >
              {{ i18n.t('noteAuditor.templateSettings.labelStrictnessDefaultSuffix') }}
            </span>
          </span>
        </template>
      </hr-option-group>
    </fieldset>
  </div>
</template>

<style lang="scss" scoped>
.auditor-template-settings__strictness {
  margin: 0;
  padding: 0;
  border: 0;
  margin-inline-start: 32px;
}
</style>
